import React, { useState } from "react";
import {
  Button,
  Field,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import { ColumnEdit20Regular } from "@fluentui/react-icons/svg/column-edit";
import { Delete20Regular } from "@fluentui/react-icons/svg/delete";
import { useTranslation } from "react-i18next";
import type {
  VstsProcessWorkItemType,
  VstsWorkItemTypeMapping,
} from "@onetasklist/shared/sources/vsts";
import type { SourceStatusMapping, Status } from "@onetasklist/shared";
import { SettingsSectionCard } from "./SettingsSectionCard";
import { SourceMultiTagPicker } from "./SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./SourceSingleTagPicker";
import { VstsColumnMappingDialog } from "./VstsColumnMappingDialog";

interface VstsStatusMappingCardProps {
  sectionOffset: number;
  title: string;
  mappings: VstsWorkItemTypeMapping[];
  availableToAdd: VstsProcessWorkItemType[];
  isLoading: boolean;
  addLabelText: string;
  addPlaceholderText: string;
  statusesToLoadPlaceholderText: string;
  doneStatusPlaceholderText: string;
  loadingText: string;
  boardStatuses: Status[];
  onAdd: (workItemType: VstsProcessWorkItemType) => void;
  onRemove: (index: number) => void;
  onSetStatusesToLoad: (index: number, stateNames: string[]) => void;
  onSetDoneStatus: (index: number, stateName?: string) => void;
  onSetStatusMappings: (index: number, mappings: SourceStatusMapping[]) => void;
}

export const VstsStatusMappingCard: React.FC<VstsStatusMappingCardProps> = ({
  sectionOffset,
  title,
  mappings,
  availableToAdd,
  isLoading,
  addLabelText,
  addPlaceholderText,
  statusesToLoadPlaceholderText,
  doneStatusPlaceholderText,
  loadingText,
  boardStatuses,
  onAdd,
  onRemove,
  onSetStatusesToLoad,
  onSetDoneStatus,
  onSetStatusMappings,
}) => {
  const { t } = useTranslation();
  const [rowStatusesQueries, setRowStatusesQueries] = useState<string[]>([]);
  const [rowDoneStatusQueries, setRowDoneStatusQueries] = useState<string[]>(
    [],
  );
  const [addQuery, setAddQuery] = useState("");
  const [openMappingRowIndex, setOpenMappingRowIndex] = useState<number | null>(
    null,
  );

  const getRowStatusesQuery = (i: number) => rowStatusesQueries[i] ?? "";
  const setRowStatusesQuery = (i: number, q: string) => {
    setRowStatusesQueries((prev) => {
      const next = [...prev];
      next[i] = q;
      return next;
    });
  };
  const getRowDoneStatusQuery = (i: number) => rowDoneStatusQueries[i] ?? "";
  const setRowDoneStatusQuery = (i: number, q: string) => {
    setRowDoneStatusQueries((prev) => {
      const next = [...prev];
      next[i] = q;
      return next;
    });
  };

  const openMappingRow =
    openMappingRowIndex !== null ? mappings[openMappingRowIndex] : undefined;

  return (
    <SettingsSectionCard sectionOffset={sectionOffset} title={title}>
      <Field orientation="horizontal" label={addLabelText}>
        <SourceSingleTagPicker
          selectedId={undefined}
          selectedLabel={undefined}
          query={addQuery}
          placeholder={addPlaceholderText}
          options={availableToAdd.map((wt) => ({
            id: wt.url ?? wt.referenceName ?? wt.name ?? "",
            label: wt.name ?? wt.referenceName ?? "",
          }))}
          onQueryChange={setAddQuery}
          onSelect={(url) => {
            const wt = availableToAdd.find(
              (w) => (w.url ?? w.referenceName ?? w.name) === url,
            );
            if (wt) {
              onAdd(wt);
              setAddQuery("");
            }
          }}
          clearSelectionText={addPlaceholderText}
        />
      </Field>

      {mappings.length > 0 && (
        <div>
          <Table size="medium">
            <TableHeader>
              <TableRow>
                <TableHeaderCell scope="col">
                  {t(
                    "board.connections.vsts.workItemTypeColumnHeader",
                    "Work item type",
                  )}
                </TableHeaderCell>
                <TableHeaderCell scope="col">
                  {t(
                    "board.connections.vsts.statusesToLoadColumnHeader",
                    "Statuses to load",
                  )}
                </TableHeaderCell>
                <TableHeaderCell scope="col">
                  {t(
                    "board.connections.vsts.doneStatusColumnHeader",
                    "Done status",
                  )}
                </TableHeaderCell>
                <TableHeaderCell scope="col">
                  {t(
                    "board.connections.vsts.columnMappingColumnHeader",
                    "Column mapping",
                  )}
                </TableHeaderCell>
                <TableHeaderCell style={{ width: 48 }} scope="col" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {mappings.map((mapping, i) => {
                const workItemTypeName =
                  mapping.workItemType.name ??
                  mapping.workItemType.referenceName ??
                  "";
                const stateOptions = (mapping.workItemType.states ?? []).map(
                  (s) => ({ id: s.name ?? "", label: s.name ?? "" }),
                );
                const hasStatusesToLoad = mapping.statusesToLoad.length > 0;
                const columnMappingButton = (
                  <Button
                    appearance="secondary"
                    size="small"
                    icon={<ColumnEdit20Regular />}
                    disabled={!hasStatusesToLoad}
                    aria-label={t(
                      "board.connections.vsts.columnMappingButtonAria",
                      "Configure column mapping for {{workItemType}}",
                      { workItemType: workItemTypeName },
                    )}
                    onClick={() => setOpenMappingRowIndex(i)}
                  >
                    {t(
                      "board.connections.vsts.columnMappingButtonLabel",
                      "Column mapping",
                    )}
                  </Button>
                );

                return (
                  <TableRow
                    key={
                      mapping.workItemType.url ??
                      mapping.workItemType.referenceName ??
                      i
                    }
                  >
                    <TableCell
                      style={{
                        verticalAlign: "top",
                        paddingTop: 8,
                        paddingBottom: 8,
                      }}
                    >
                      <Text weight="semibold">{workItemTypeName}</Text>
                    </TableCell>

                    <TableCell
                      style={{
                        verticalAlign: "top",
                        paddingTop: 8,
                        paddingBottom: 8,
                      }}
                    >
                      <SourceMultiTagPicker
                        selectedIds={mapping.statusesToLoad.map(
                          (s) => s.name ?? "",
                        )}
                        query={getRowStatusesQuery(i)}
                        placeholder={statusesToLoadPlaceholderText}
                        options={stateOptions}
                        onQueryChange={(q) => setRowStatusesQuery(i, q)}
                        onSelectionChange={(names) =>
                          onSetStatusesToLoad(i, names)
                        }
                        minQueryLength={0}
                        typeToSearchText={statusesToLoadPlaceholderText}
                      />
                    </TableCell>

                    <TableCell
                      style={{
                        verticalAlign: "top",
                        paddingTop: 8,
                        paddingBottom: 8,
                      }}
                    >
                      <SourceSingleTagPicker
                        selectedId={mapping.doneStatus?.name ?? undefined}
                        selectedLabel={mapping.doneStatus?.name ?? undefined}
                        query={getRowDoneStatusQuery(i)}
                        placeholder={doneStatusPlaceholderText}
                        options={stateOptions}
                        onQueryChange={(q) => setRowDoneStatusQuery(i, q)}
                        onSelect={(name) => onSetDoneStatus(i, name)}
                        clearSelectionText={doneStatusPlaceholderText}
                      />
                    </TableCell>

                    <TableCell
                      style={{
                        verticalAlign: "top",
                        paddingTop: 8,
                        paddingBottom: 8,
                      }}
                    >
                      {hasStatusesToLoad ? (
                        columnMappingButton
                      ) : (
                        <Tooltip
                          content={t(
                            "board.connections.vsts.columnMappingDisabledHint",
                            "Select statuses to load before mapping columns",
                          )}
                          relationship="label"
                        >
                          {columnMappingButton}
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell
                      style={{
                        verticalAlign: "top",
                        paddingTop: 8,
                        paddingBottom: 8,
                      }}
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Delete20Regular />}
                        aria-label={t(
                          "board.connections.vsts.removeMapping",
                          "Remove",
                        )}
                        onClick={() => onRemove(i)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {isLoading && <Text>{loadingText}</Text>}

      {openMappingRow && openMappingRowIndex !== null && (
        <VstsColumnMappingDialog
          open
          onClose={() => setOpenMappingRowIndex(null)}
          title={t(
            "board.connections.vsts.columnMappingDialogTitle",
            "{{workItemType}} column mapping",
            {
              workItemType:
                openMappingRow.workItemType.name ??
                openMappingRow.workItemType.referenceName ??
                "",
            },
          )}
          items={openMappingRow.statusesToLoad.map((s) => ({
            id: `${openMappingRow.workItemType.name ?? ""}:${s.name ?? ""}`,
            name: s.name ?? "",
          }))}
          mappings={openMappingRow.statusMappings ?? []}
          boardStatuses={boardStatuses}
          onChange={(sm) => onSetStatusMappings(openMappingRowIndex, sm)}
        />
      )}
    </SettingsSectionCard>
  );
};
